import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { displayDateTime } from 'reducers/TimeReducer';
import { handleError } from 'reducers/ErrorReducer';
import { makeStyles } from '@material-ui/core/styles';

import SearchDatePicker from '../components/SearchDatePicker';
import { showDisplayDataWindow } from 'reducers/DialogsReducer';
import { searchRotations } from 'reducers/SearchReducer';
import QuickFilterSearch from '../components/QuickFilterSearch';
import CheckIcon from '@material-ui/icons/Check';

import SearchHotKeys from 'Search/SearchHotKeys';
import XGrid3 from 'components/XGrid3';

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  panel: {
    '& .MuiDataGridPanelFooter-root': {
      display: 'none',
    },
  },
}));

const bool = (params) => (params.value ? <CheckIcon /> : <span></span>);

const today = new Date();
const startDate = new Date();
startDate.setDate(startDate.getDate() - 70);

const defaultColumns = [
  { field: 'Rotation', headerName: 'Rotation', width: 100, hide: false },
  { field: 'ActionReason', headerName: 'Action Reason', width: 120, hide: false },
  { field: 'Action', headerName: 'Action', width: 100, hide: false },
  { field: 'Unit', headerName: 'Unit', width: 120, hide: false },
  { field: 'AgencyID', headerName: 'Agency ID', width: 120, hide: false },
  { field: 'ContactType', headerName: 'Primary Contact Type', width: 120, hide: false },
  { field: 'ContactName', headerName: 'Contact Name', width: 120, hide: false },
  { field: 'ContactInfo', headerName: 'Info', width: 100, hide: false },
  { field: 'EventID', headerName: 'Event ID', width: 120, hide: false },
  { field: 'CallType', headerName: 'Event Type', width: 120, hide: false },
  {
    field: 'Created',
    headerName: 'Created',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 150, hide: false },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 150, hide: false },
  {
    field: 'IsDeleted',
    headerName: 'Is Deleted',
    width: 120,
    format: 'bool',
    renderCell: bool,
    hide: false,
  },
  { field: 'DeletedBy', headerName: 'Deleted By', width: 120, hide: false },
  { field: 'DeletedIP', headerName: 'Deleted IP', width: 120, hide: false },
];

function RotationHistory(props) {
  const classes = useStyles();
  const { filter } = props;
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [dateFrom, setDateFrom] = useState(startDate);
  const [dateTo, setDateTo] = useState(today);
  const [loaded, setLoaded] = useState(false);
  const [selection, setSelection] = useState(null);
  const [tables, setTables] = useState(null);
  const [colsSeq, setColsSeq] = useState([]);
  const [columns, setColumns] = useState(defaultColumns);
  const [kbdKey, setKbdKey] = useState(null);
  const kbdTimeoutRef = useRef(0);

  useEffect(() => {
    if (kbdKey) handleKeyPress();
    kbdTimeoutRef.current = setTimeout(() => {
      setKbdKey(null);
    }, 500);
  }, [kbdKey]);

  useEffect(() => {
    search();
    // eslint-disable-next-line
  }, [filter]);

  const handleKeyPress = () => {
    switch (kbdKey) {
      case 'F5':
        search();
        break;
      case 'Ctrl+KeyP':
        viewDetails();
        break;
    }
  };

  const processData = (data) => {
    return data.map((row) => {
      return { ...row, id: row.ptsRotationHistoryID };
    });
  };

  const search = () => {
    setLoaded(false);
    searchRotations(dateFrom, dateTo, filter)
      .then((result) => setRows(processData(result)))
      .catch(props.handleError)
      .finally(() => setLoaded(true));
  };

  const viewDetails = () => {
    const item = rows.find((row) => row.id === selection);
    if (!item) return;
    props.showDisplayDataWindow({
      data: filterData(item),
      title: `Rotation Details - ${item.Rotation}`,
    });
  };

  const filterData = (rawData) => {
    const {
      Rotation,
      ActionReason,
      Action,
      Unit,
      AgencyID,
      Division,
      Zone,
      ContactType,
      ContactName,
      ContactInfo,
      EventID,
      CallType,
      Received,
      Queued,
      Dispatched,
      Enroute,
      Arrived,
      Completed,
      UnderControl,
      Cancelled,
      Created,
      CreatedBy,
      Updated,
      UpdatedBy,
    } = rawData;

    return {
      Rotation,
      ActionReason,
      Action,
      Unit,
      AgencyID,
      Division,
      Zone,
      ContactType,
      ContactName,
      ContactInfo,
      EventID,
      CallType,
      Received,
      Queued,
      Dispatched,
      Enroute,
      Arrived,
      Completed,
      UnderControl,
      Cancelled,
      Created,
      CreatedBy,
      Updated,
      UpdatedBy,
    };
  };

  return (
    <div>
      <h5>Rotation History</h5>
      <QuickFilterSearch rows={rows} setFilteredRows={setFilteredRows} columns={columns} />
      <SearchDatePicker
        search={search}
        viewDetails={typeof selection === 'number' ? viewDetails : null}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        printTitle="Rotation History"
        filteredRows={filteredRows}
        columns={columns}
        kbdKey={kbdKey}
      />
      <XGrid3
        name="rotationHistory"
        columns={columns}
        setColumns={setColumns}
        rows={filteredRows}
        loading={!loaded}
        setSelection={setSelection}
      />
      <SearchHotKeys setKey={setKbdKey} />
    </div>
  );
}

export default connect(null, {
  handleError,
  showDisplayDataWindow,
})(RotationHistory);
